import React, { useState, useEffect, useContext } from 'react';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import { Table, TableBody, TableContainer, TableRow, TableCell } from '@mui/material';
import {
  CircularProgress, // Import CircularProgress
  Box, // Import Box
} from '@mui/material';

import { UserContext } from 'context/userContext';
import { RecentContext } from 'context/recentContext';
import { useNavigate } from 'react-router-dom';

function RecentProducts({ orgs, onEdit, onDelete, refreshOrgs }) {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [localProducts, setLocalProducts] = useState([]); // Local recent products
  const [loading, setLoading] = useState(true); // Add loading state

  const { userId } = useContext(UserContext);
  const { recentProducts, setPendingProductToOpen } = useContext(RecentContext);
  const navigate = useNavigate();

  // Update localOrgs whenever orgs prop changes
  useEffect(() => {
    // Prefer context recentProducts; fallback to props for any placeholder data
    setLocalProducts((recentProducts && recentProducts.length > 0 ? recentProducts : orgs) || []);
  }, [recentProducts, orgs]);

  useEffect(() => {
    setColumns([
      { name: 'name', align: 'left', width: '40%' },
      { name: 'org', align: 'left', width: '60%' },
    ]);

    const buildRows = async () => {
      setLoading(true); // Start loading
      try {
        const rowData = await Promise.all(
          localProducts.slice(0, 5).map(async (entry) => {
            console.log('ENTRY', entry);
            const displayName = entry.name || entry.raw?.product?.name || 'Unknown Product';
            const orgName = entry.org_name || entry.raw?.org?.name || 'Unknown Org';
            return {
              name: (
                <ArgonTypography
                  variant="caption"
                  color="secondary"
                  fontWeight="medium"
                  sx={{
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                  title={displayName}
                >
                  {displayName}
                </ArgonTypography>
              ),
              org: (
                <ArgonTypography variant="caption" color="text.secondary" fontWeight="regular">
                  {orgName}
                </ArgonTypography>
              ),
              onClick: () => {
                // Pass the product to Products page to open details
                setPendingProductToOpen(entry.raw || { product: entry });
                navigate('/catalogue/products');
              },
            };
          })
        );
        setRows(rowData);
      } catch (error) {
        console.error('Error building rows:', error);
        // Optionally show an error message to the user here, e.g., using a state variable
      } finally {
        setLoading(false); // Stop loading, regardless of success/failure
      }
    };

    buildRows();
  }, [localProducts, onEdit, userId, refreshOrgs, navigate, setPendingProductToOpen]); // Include dependencies

  return (
    <ArgonBox mb={1} mt={2}>
      <ArgonBox>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <ArgonBox component="thead">
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      align={column.align}
                      sx={{ width: column.width, fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                      {column.name.toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </ArgonBox>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={index}
                    onClick={row.onClick}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <TableCell key={colIndex} align={column.align} sx={{ width: column.width }}>
                        {row[column.name]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ArgonBox>
    </ArgonBox>
  );
}

export default RecentProducts;
